import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MemberHeader from "@/components/MemberHeader";
import AuthImage from "@/components/AuthImage";
import { Calendar, MapPin, Users, Euro, Repeat, Send, ArrowLeft, Lock, UserCircle, ExternalLink } from "lucide-react";

function money(v) {
  if (v == null || v === 0) return "Gratuito";
  return `${Number(v).toFixed(2)} €`;
}

function mapsUrl(location) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${location}, Porto, Portugal`)}`;
}

function calRange(plan) {
  const date = (plan.date || "").replaceAll("-", "");
  const t = (plan.time || "18:00").replace(":", "");
  const start = `${date}T${t.padEnd(4, "0")}00`;
  const endH = String((parseInt((plan.time || "18:00").slice(0, 2)) + 2) % 24).padStart(2, "0");
  const end = `${date}T${endH}${(plan.time || "18:00").slice(3, 5)}00`;
  return { start, end };
}

function googleCalUrl(plan) {
  const { start, end } = calRange(plan);
  const p = new URLSearchParams({
    action: "TEMPLATE", text: `The Social Club · ${plan.title}`,
    dates: `${start}/${end}`, details: plan.description || "Plano do The Social Club",
    location: `${plan.location}, Porto`,
  });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function outlookCalUrl(plan) {
  const iso = plan.date ? `${plan.date}T${plan.time || "18:00"}:00` : "";
  const p = new URLSearchParams({
    path: "/calendar/action/compose", rru: "addevent",
    subject: `The Social Club · ${plan.title}`, startdt: iso,
    location: `${plan.location}, Porto`, body: plan.description || "Plano do The Social Club",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${p.toString()}`;
}

export default function PlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [plan, setPlan] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const canChat = plan && (isAdmin || plan.joined);

  const loadPlan = async () => {
    try {
      const { data } = await api.get(`/plans/${id}`);
      setPlan(data);
    } catch {
      toast.error("Plano não encontrado.");
      navigate("/area");
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/plans/${id}/messages`);
      setMessages(data);
    } catch { /* not a member yet */ }
  };

  useEffect(() => { loadPlan(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (!canChat) return;
    loadMessages();
    const t = setInterval(loadMessages, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [canChat]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const join = async () => {
    setBusy(true);
    try {
      await api.post(`/plans/${id}/join`);
      toast.success("Estás dentro! Vemo-nos por lá.");
      await loadPlan();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await api.post(`/plans/${id}/leave`);
      toast("Saíste do plano.");
      await loadPlan();
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const body = text;
    setText("");
    try {
      const { data } = await api.post(`/plans/${id}/messages`, { text: body });
      setMessages((m) => [...m, data]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!plan) return <div className="min-h-screen bg-[#FCFCFA]"><MemberHeader /></div>;

  const full = plan.is_full && !plan.joined;

  return (
    <div className="min-h-screen bg-[#FCFCFA]">
      <MemberHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <Link to="/area" className="inline-flex items-center gap-2 sc-label text-[#6E5F58] hover:text-[#48020C]"><ArrowLeft className="w-4 h-4" /> Voltar aos planos</Link>

        <div className="grid lg:grid-cols-5 gap-8 mt-6">
          {/* Plan info */}
          <div className="lg:col-span-2">
            <div className="bg-[#F4F1EA] border border-[#E3DED2] rounded-2xl overflow-hidden sticky top-28">
              {plan.photo_path && <AuthImage path={plan.photo_path} className="w-full h-52 object-cover" alt={plan.title} />}
              <div className="p-7">
              <span className="sc-label text-[#48020C]">{plan.category}</span>
              <h1 className="font-serif text-3xl sm:text-4xl text-[#26130E] mt-2 leading-tight">{plan.title}</h1>
              {plan.created_by_name && (
                <p className="flex items-center gap-2 text-sm text-[#6E5F58] mt-3"><UserCircle className="w-4 h-4 text-[#48020C]" /> Criado por {plan.created_by_name}</p>
              )}
              {plan.description && <p className="text-[#6E6359] mt-4 leading-relaxed">{plan.description}</p>}
              <div className="mt-6 space-y-3 text-[#3A1F19]">
                <p className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[#48020C]" /> {plan.date}{plan.time ? ` · ${plan.time}` : ""}</p>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#48020C] mt-0.5 flex-shrink-0" />
                  <div>
                    <div>{plan.location}</div>
                    <a href={mapsUrl(plan.location)} target="_blank" rel="noopener noreferrer" data-testid="plan-maps-link" className="inline-flex items-center gap-1.5 text-sm text-[#48020C] underline underline-offset-4 hover:text-[#48020C] transition-colors mt-1">
                      Ver no Google Maps <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
                <p className="flex items-center gap-3"><Users className="w-5 h-5 text-[#48020C]" /> {plan.taken}/{plan.seats} lugares</p>
                <p className="flex items-center gap-3"><Euro className="w-5 h-5 text-[#48020C]" /> {money(plan.value)}</p>
                {plan.recurrence !== "pontual" && <p className="flex items-center gap-3 text-[#48020C]"><Repeat className="w-5 h-5" /> {plan.recurrence === "semanal" ? "Semanal" : "Mensal"} · plano independente</p>}
              </div>

              <div className="mt-7">
                {plan.joined ? (
                  <button data-testid="leave-plan-btn" onClick={leave} disabled={busy} className="w-full border border-[#8C2222] text-[#8C2222] hover:bg-[#8C2222] hover:text-white rounded-full py-3.5 transition-colors">Sair do plano</button>
                ) : full ? (
                  <div className="text-center">
                    <button data-testid="soldout-btn" disabled className="w-full bg-[#F9ECEC] text-[#8C2222] rounded-full py-3.5 cursor-not-allowed">Esgotado</button>
                    <p className="text-sm text-[#6E5F58] mt-3">Este plano já está completo.<br />Fica atenta aos próximos.</p>
                  </div>
                ) : (
                  <button data-testid="join-plan-btn" onClick={join} disabled={busy} className="w-full bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full py-3.5 transition-colors">Aderir a este plano</button>
                )}
              </div>
              {plan.joined && (
                <div className="mt-4 border-t border-[#E3DED2] pt-4">
                  <div className="sc-label text-[#48020C] mb-2">Adicionar à agenda</div>
                  <div className="flex flex-wrap gap-2">
                    <a href={googleCalUrl(plan)} target="_blank" rel="noopener noreferrer" data-testid="gcal-link" className="text-sm border border-[#E3DED2] hover:border-[#48020C] rounded-full px-4 py-2 transition-colors">Google</a>
                    <a href={outlookCalUrl(plan)} target="_blank" rel="noopener noreferrer" data-testid="outlook-link" className="text-sm border border-[#E3DED2] hover:border-[#48020C] rounded-full px-4 py-2 transition-colors">Outlook</a>
                  </div>
                </div>
              )}
              </div>

            </div>
          </div>

          {/* Chat */}
          <div className="lg:col-span-3">
            <div className="bg-[#F4F1EA] border border-[#E3DED2] rounded-2xl flex flex-col h-[70vh]">
              <div className="px-6 py-4 border-b border-[#E3DED2]">
                <h2 className="font-serif text-2xl text-[#26130E]">Chat do plano</h2>
                <p className="text-xs text-[#6E5F58]">{isAdmin ? "A ver como administradora" : "Conversa privada entre inscritas"}</p>
              </div>

              {!canChat ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                  <Lock className="w-8 h-8 text-[#48020C]" />
                  <p className="font-serif text-xl text-[#48020C] mt-4">Chat exclusivo</p>
                  <p className="text-[#6E6359] mt-2">Adere ao plano para entrares na conversa.</p>
                </div>
              ) : (
                <>
                  <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {messages.length === 0 && <p className="text-center text-[#6E5F58] text-sm">Ainda não há mensagens. Sê a primeira!</p>}
                    {messages.map((m) => {
                      const mine = m.user_id === user.id;
                      return (
                        <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
                          <AuthImage path={m.photo_path} fallbackName={m.user_name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 text-xs" />
                          <div className={`max-w-[75%] ${mine ? "items-end text-right" : ""} flex flex-col`}>
                            <span className="text-xs text-[#6E5F58] mb-1">{m.user_name}{m.is_admin ? " · Admin" : ""}</span>
                            <div className={`rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#48020C] text-[#FCFCFA]" : m.is_admin ? "bg-[#F7EEC7] text-[#26130E] border border-[#48020C]/40" : "bg-[#FCFCFA] text-[#1A1412] border border-[#E3DED2]"}`}>
                              {m.text}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                  <form onSubmit={send} className="p-4 border-t border-[#E3DED2] flex gap-3">
                    <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Escreve uma mensagem…"
                      className="flex-1 bg-[#FCFCFA] border border-[#E3DED2] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#48020C]" />
                    <button data-testid="chat-send-btn" type="submit" className="bg-[#48020C] hover:bg-[#26130E] text-[#F7EEC7] rounded-full w-12 h-12 flex items-center justify-center transition-colors">
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
